import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Copy, Mail } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useTenant } from '../hooks/useTenantContext';
import { getProfile } from '../services/api';
import Button from '../components/ui/Button';
import { APP_TILES } from '../config/appCatalog';
import { Textarea as PrimitiveTextarea } from '../components/ui/textarea';

const BETA_INBOX = import.meta.env.VITE_BETA_REQUEST_EMAIL?.trim() || '';

/**
 * Lets users request early access to a not-yet-launched app from the portal.
 * Prefers mailto when VITE_BETA_REQUEST_EMAIL is set; otherwise copy-to-clipboard.
 */
const BetaAccessPage: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const tileId = (searchParams.get('tile') || '').trim();
  const { displayLabel, tenantSlug } = useTenant();
  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: getProfile,
    enabled: !!user,
  });

  const [notes, setNotes] = useState('');
  const [copied, setCopied] = useState(false);

  const tileMeta = useMemo(
    () => APP_TILES.find((t) => t.id === tileId),
    [tileId]
  );
  const appLabel = tileMeta?.title ?? 'Platform capability';

  React.useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = '/login';
    }
  }, [user, authLoading]);

  const requestBody = useMemo(() => {
    const lines = [
      'Slide Rule — beta access request',
      `Capability: ${appLabel} (tile id: ${tileId || 'unknown'})`,
      `User: ${profile?.email ?? user?.email ?? 'unknown'}`,
      `Organization / tenant: ${displayLabel}${tenantSlug ? ` (${tenantSlug})` : ''}`,
      '',
      'Notes:',
      notes.trim() || '(none)',
    ];
    return lines.join('\n');
  }, [
    appLabel,
    tileId,
    profile?.email,
    user?.email,
    displayLabel,
    tenantSlug,
    notes,
  ]);

  const openMailto = () => {
    if (!BETA_INBOX) return;
    const subject = encodeURIComponent(`Beta access: ${appLabel}`);
    const body = encodeURIComponent(requestBody);
    window.location.href = `mailto:${BETA_INBOX}?subject=${subject}&body=${body}`;
  };

  const copyRequest = async () => {
    try {
      await navigator.clipboard.writeText(requestBody);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
    }
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-ink-subtle">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-ink">
      <div className="max-w-lg mx-auto px-4 py-12">
        <Link
          to="/portal"
          className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to apps
        </Link>

        <h1 className="text-2xl font-semibold tracking-tight">
          Request beta access
        </h1>
        <p className="mt-2 text-ink-subtle">
          You selected <span className="text-ink font-medium">{appLabel}</span>,
          which is not generally available yet. Tell us how you would use it and
          we will follow up.
        </p>

        <label className="block mt-8 text-sm font-medium text-ink">
          Anything we should know?
          <PrimitiveTextarea
            unstyled
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={5}
            className="mt-2 w-full px-3 py-2 rounded-lg border border-border bg-surface text-ink text-sm placeholder:text-ink-subtle focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Use case, timeline, team size, integrations…"
          />
        </label>

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          {BETA_INBOX ? (
            <Button type="button" className="gap-2" onClick={openMailto}>
              <Mail className="w-4 h-4" />
              Open email draft
            </Button>
          ) : null}
          <Button
            type="button"
            variant={BETA_INBOX ? 'secondary' : 'primary'}
            className="gap-2"
            onClick={() => void copyRequest()}
          >
            <Copy className="w-4 h-4" />
            {copied ? 'Copied' : 'Copy request text'}
          </Button>
        </div>

        {!BETA_INBOX && (
          <p className="mt-4 text-xs text-ink-subtle">
            Set <code className="text-[11px]">VITE_BETA_REQUEST_EMAIL</code> in
            your frontend env to enable one-click email drafts to your team
            inbox.
          </p>
        )}
      </div>
    </div>
  );
};

export default BetaAccessPage;
